import React, { useState, useEffect } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Home, Wrench, Clock, Upload, MessageSquare, Send } from "lucide-react";
import { offerCalculatorData } from "../data/mockData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OfferSubmission = () => {
  const [formData, setFormData] = useState({
    address: "",
    city: "",
    state: "",
    zipCode: "",
    propertyType: "",
    bedrooms: "",
    bathrooms: "",
    squareFootage: "",
    condition: "",
    yearBuilt: "",
    timeline: "",
    reasonForSelling: "",
    otherReason: "",
    propertyDescription: "",
    name: "",
    phone: "",
    email: "",
    photoInfo: "",
    photoLink: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOtherReason, setShowOtherReason] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === "reasonForSelling") {
      setShowOtherReason(value === "Other");
      if (value !== "Other") {
        setFormData(prev => ({ ...prev, otherReason: "" }));
      }
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Limit to 30 photos
    if (files.length > 30) {
      alert("Maximum 30 photos allowed. Please select fewer files.");
      e.target.value = ""; // Clear the input
      return;
    }
    
    setSelectedFiles(files);

    const fileNames = files.map(f => f.name).join(", ");
    setFormData(prev => ({
      ...prev,
      photoInfo: `${files.length} photo(s) selected: ${fileNames}`
    }));
  };

  const submitForReview = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key] || "");
      });

      // 🔥 FIX 1: Add multiple photos - append each file individually
      if (selectedFiles && selectedFiles.length > 0) {
        selectedFiles.forEach(file => {
          formDataToSend.append("files", file);
        });
      }

      const response = await fetch(`${API}/submit-inquiry`, {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        alert(result.detail || 'Error submitting your request');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error submitting your request. Please try calling us directly at 1-888-334-3524');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 🔥 FIX 2: Auto-scroll to thank-you section when submitted
  useEffect(() => {
    if (isSubmitted) {
      const thankYouSection = document.getElementById("thank-you");
      if (thankYouSection) {
        thankYouSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [isSubmitted]);

  if (isSubmitted) {
    return (
      <section id="thank-you" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-12 text-center bg-gradient-to-br from-green-50 to-blue-50 border-green-200">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
              <Send className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Thank you!
            </h2>
            <p className="text-2xl text-gray-700 mb-8">
              One of our team members will be in touch on next steps right away!
            </p>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section id="offer-submission" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Submit Your Property for Review
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Tell us about your property and we'll provide you with a fair cash offer within 24 hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar - Key Benefits */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="p-6 bg-gradient-to-br from-blue-50 to-green-50 border-blue-200">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <Clock className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Fast Response</h4>
                    <p className="text-sm text-gray-600">Get your offer within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                    <Home className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">No Repairs Needed</h4>
                    <p className="text-sm text-gray-600">We buy homes as-is</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                    <Wrench className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Zero Fees</h4>
                    <p className="text-sm text-gray-600">No commissions or hidden costs</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">No Obligation</h4>
                    <p className="text-sm text-gray-600">Free consultation, no pressure</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
              <h4 className="font-bold text-lg mb-3">Need Help?</h4>
              <p className="text-sm text-gray-300 mb-4">
                Call us directly for immediate assistance
              </p>
              <a
                href="tel:1-888-334-3524"
                className="block w-full bg-white text-gray-900 font-bold py-3 px-4 rounded-lg text-center hover:bg-gray-100 transition-colors"
              >
                1-888-334-3524
              </a>
            </Card>
          </div>

          {/* Main Form */}
          <div className="lg:col-span-3">
            <Card className="p-8">
              <form onSubmit={submitForReview} className="space-y-8">
                {/* Property Location */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Home className="h-6 w-6 mr-2 text-blue-600" />
                    Property Location
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="address"
                      placeholder="Street Address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      name="city"
                      placeholder="City"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select State</option>
                      {offerCalculatorData.states.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      name="zipCode"
                      placeholder="ZIP Code"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Property Details */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Wrench className="h-6 w-6 mr-2 text-green-600" />
                    Property Details
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <select
                      name="propertyType"
                      value={formData.propertyType}
                      onChange={handleInputChange}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Property Type</option>
                      <option value="Single Family">Single Family</option>
                      <option value="Multi-Family">Multi-Family</option>
                      <option value="Condo">Condo</option>
                      <option value="Townhouse">Townhouse</option>
                      <option value="Land">Land</option>
                      <option value="Other">Other</option>
                    </select>

                    <input
                      type="number"
                      name="bedrooms"
                      placeholder="Bedrooms (Optional)"
                      value={formData.bedrooms}
                      onChange={handleInputChange}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />

                    <input
                      type="number"
                      name="bathrooms"
                      step="0.5"
                      min="0.5"
                      placeholder="Bathrooms (Optional)"
                      value={formData.bathrooms}
                      onChange={handleInputChange}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />

                    <input
                      type="number"
                      name="squareFootage"
                      placeholder="Square Footage (Optional)"
                      value={formData.squareFootage}
                      onChange={handleInputChange}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />

                    <input
                      type="number"
                      name="yearBuilt"
                      placeholder="Year Built (Optional)"
                      value={formData.yearBuilt}
                      onChange={handleInputChange}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />

                    <select
                      name="condition"
                      value={formData.condition}
                      onChange={handleInputChange}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Property Condition</option>
                      <option value="Excellent">Excellent</option>
                      <option value="Good">Good</option>
                      <option value="Fair">Fair</option>
                      <option value="Needs Work">Needs Work</option>
                      <option value="Major Repairs">Major Repairs</option>
                    </select>
                  </div>
                </div>

                {/* Selling Details */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Clock className="h-6 w-6 mr-2 text-purple-600" />
                    Selling Details
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">When do you need to sell?</option>
                      <option value="ASAP">As soon as possible</option>
                      <option value="1-2 weeks">1-2 weeks</option>
                      <option value="1 month">Within 1 month</option>
                      <option value="3 months">Within 3 months</option>
                      <option value="Just exploring">Just exploring options</option>
                    </select>

                    <select
                      name="reasonForSelling"
                      value={formData.reasonForSelling}
                      onChange={handleInputChange}
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Reason for selling?</option>
                      <option value="Relocation">Relocation</option>
                      <option value="Divorce">Divorce</option>
                      <option value="Financial">Financial difficulties</option>
                      <option value="Inherited">Inherited property</option>
                      <option value="Downsizing">Downsizing</option>
                      <option value="Upgrading">Upgrading</option>
                      <option value="Other">Other</option>
                    </select>

                    {showOtherReason && (
                      <input
                        type="text"
                        name="otherReason"
                        placeholder="Please specify"
                        value={formData.otherReason}
                        onChange={handleInputChange}
                        className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent md:col-span-2"
                      />
                    )}
                  </div>
                </div>

                {/* Property Description */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <MessageSquare className="h-6 w-6 mr-2 text-orange-600" />
                    Additional Information
                  </h3>
                  <textarea
                    name="propertyDescription"
                    placeholder="Tell us more about your property, any repairs needed, or special circumstances... (Optional)"
                    value={formData.propertyDescription}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Photo Upload */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Upload className="h-6 w-6 mr-2 text-blue-600" />
                    Property Photos (Optional)
                  </h3>
                  
                  {/* File Upload */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors mb-4">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      id="photo-upload"
                    />
                    <label
                      htmlFor="photo-upload"
                      className="cursor-pointer inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                    >
                      Choose Photos
                    </label>
                    <p className="text-sm text-gray-500 mt-3">
                      Upload up to 30 photos (JPG, PNG) - Hold Ctrl/Cmd to select multiple
                    </p>
                    {formData.photoInfo && (
                      <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-700 font-medium">
                          ✓ {formData.photoInfo}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Google Drive / Dropbox Link */}
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Or share a link to your photos:
                    </label>
                    <input
                      type="url"
                      name="photoLink"
                      placeholder="Paste Google Drive, Dropbox, or other photo link here..."
                      value={formData.photoLink}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Tip: Make sure the link is set to "Anyone with the link can view"
                    </p>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Your Contact Information
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="Full Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white py-4 text-lg font-bold rounded-lg shadow-lg hover:shadow-xl transition-all"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <Send className="h-5 w-5 mr-2" />
                        Submit Property Information
                      </span>
                    )}
                  </Button>
                  <p className="text-sm text-gray-500 text-center mt-4">
                    Your information is secure and confidential. No obligation.
                  </p>
                </div>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OfferSubmission;
